import React, { useState } from "react";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Form,
  TvAlert,
  TvButton,
  TvDiv,
  TvIconButton,
  TvInput,
  TvLabel,
  TvSelect,
  TvOption,
  TvSmall,
  Spinner,
} from "@trustvid/design-system";
import { toast } from "react-toastify";
import { RequestSelfSignup } from "../../../services/auth";
import { beginGoogleSso, isGoogleSsoUiEnabled } from "../../../utils/googleSso";
import {
  COUNTRIES,
  CURRENCY_OPTIONS,
  DEFAULT_COUNTRY,
  DEFAULT_CURRENCY,
  defaultCurrencyForCountry,
} from "../../../constants/geoBilling";
import AuthSplitLayout from "./AuthSplitLayout";
import AuthGoogleSsoButton from "./AuthGoogleSsoButton";
import AuthFormDivider from "./AuthFormDivider";
import AuthContinueWithEmailButton from "./AuthContinueWithEmailButton";
import { AUTH_FIELD_LABEL_CLASS, AUTH_GOOGLE_SSO_COPY, REGISTER_PAGE_COPY } from "./loginPageContent";
import { validatePassword } from "../../../utils/password-validation";

const Register = () => {
  document.title = REGISTER_PAGE_COPY.pageTitle;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const googleSsoEnabled = isGoogleSsoUiEnabled();

  const validation = useFormik({
    initialValues: {
      email: "",
      name: "",
      accountName: "",
      country: DEFAULT_COUNTRY,
      currency: DEFAULT_CURRENCY,
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email format")
        .required("Please Enter Your Email"),
      name: Yup.string().required("Please Enter Your Name"),
      accountName: Yup.string().required("Please Enter Your Account Name"),
      country: Yup.string().required("Please select your country"),
      currency: Yup.string().required("Please select your currency"),
      password: Yup.string()
        .nullable()
        .test("is-valid-password", null, function validateSignupPassword(value) {
          const result = validatePassword(value);
          if (result === true) {
            return true;
          }
          const errors = result.split(", ");
          return this.createError({
            message: (
              <ul className="text-danger mb-0 ps-3">
                {errors.map((ruleError, index) => (
                  <li key={index}>{ruleError}</li>
                ))}
              </ul>
            ),
          });
        }),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError("");
      setSuccessMessage("");
      try {
        const response = await RequestSelfSignup({
          email: values.email,
          name: values.name,
          password: values.password,
          account_name: values.accountName,
          country: values.country,
          currency: values.currency,
        });
        if (response?.error) {
          setError(response.error);
          return;
        }
        setSuccessMessage(response?.message || "Signup request accepted. Please verify your email.");
      } catch {
        setError("Unable to submit signup request. Please try again.");
      } finally {
        setLoading(false);
      }
    },
  });

  const signupEmailSent = Boolean(successMessage);

  const hasFormValues =
    validation.values.email.trim().length > 0 &&
    validation.values.name.trim().length > 0 &&
    validation.values.accountName.trim().length > 0 &&
    validation.values.country.length > 0 &&
    validation.values.currency.length > 0 &&
    validation.values.password.length > 0;

  const handleChangeWithTrim = (event) => {
    const { name, value } = event.target;
    validation.setFieldValue(name, value.trimStart());
  };

  const handleCountryChange = (event) => {
    const nextCountry = event.target.value;
    validation.setFieldValue("country", nextCountry);
    validation.setFieldValue("currency", defaultCurrencyForCountry(nextCountry));
  };

  return (
    <AuthSplitLayout
      pageTestId="register-page"
      cardTestId="register-form-card"
      brandTestId="auth-platform-brand"
      showCardHeading={false}
      cardTitle={REGISTER_PAGE_COPY.pageTitle}
      welcomeHeading={signupEmailSent ? REGISTER_PAGE_COPY.checkEmailHeading : REGISTER_PAGE_COPY.pageTitle}
      welcomeHeadingTestId="register-card-title"
      cardSubtitle={signupEmailSent ? null : REGISTER_PAGE_COPY.cardSubtitle}
      featurePanelHeading={REGISTER_PAGE_COPY.featurePanelHeading}
    >
      {signupEmailSent ? (
        <TvDiv data-testid="register-check-email" className="tv-login-page__form">
          <TvAlert color="success" className="mb-3" data-testid="register-success-alert" fade={false}>
            {successMessage}
          </TvAlert>
          <TvDiv className="text-center text-lg-start">
            <Link to="/login" className="tv-login-page__text-link fw-semibold text-decoration-none">
              Back to sign in
            </Link>
          </TvDiv>
        </TvDiv>
      ) : (
      <>
      <AuthGoogleSsoButton
        testId="register-google-sso"
        enabled={googleSsoEnabled}
        disabled={loading}
        onClick={() => {
          if (!googleSsoEnabled) {
            toast.info("Google sign-up is not enabled yet.");
            return;
          }
          beginGoogleSso({
            intent: "signup",
            accountName: validation.values.accountName || undefined,
            country: validation.values.country,
            currency: validation.values.currency,
          });
        }}
      />
      <AuthFormDivider>{AUTH_GOOGLE_SSO_COPY.orDivider}</AuthFormDivider>
      {showEmailForm ? (
      <Form onSubmit={validation.handleSubmit} data-testid="register-form" className="tv-login-page__form">
        {error ? (
          <TvAlert color="danger" className="mb-3" data-testid="register-error-alert" fade={false}>
            {error}
          </TvAlert>
        ) : null}

        <TvDiv className="mb-3">
          <TvLabel htmlFor="register-name" className={AUTH_FIELD_LABEL_CLASS}>
            Full Name
          </TvLabel>
          <TvInput
            id="register-name"
            name="name"
            type="text"
            autoComplete="name"
            placeholder="Enter your full name"
            aria-label="Full Name"
            className="tv-login-page__field"
            onChange={handleChangeWithTrim}
            onBlur={validation.handleBlur}
            value={validation.values.name}
            invalid={validation.touched.name && !!validation.errors.name}
          />
          {validation.touched.name && validation.errors.name ? (
            <TvSmall className="text-danger d-block mt-1">{validation.errors.name}</TvSmall>
          ) : null}
        </TvDiv>

        <TvDiv className="mb-3">
          <TvLabel htmlFor="register-account-name" className={AUTH_FIELD_LABEL_CLASS}>
            Company / Account Name
          </TvLabel>
          <TvInput
            id="register-account-name"
            name="accountName"
            type="text"
            autoComplete="off"
            placeholder="Enter your company name"
            aria-label="Company / Account Name"
            className="tv-login-page__field"
            onChange={handleChangeWithTrim}
            onBlur={validation.handleBlur}
            value={validation.values.accountName}
            invalid={validation.touched.accountName && !!validation.errors.accountName}
          />
          {validation.touched.accountName && validation.errors.accountName ? (
            <TvSmall className="text-danger d-block mt-1">{validation.errors.accountName}</TvSmall>
          ) : null}
        </TvDiv>

        <TvDiv className="mb-3">
          <TvLabel htmlFor="register-country" className={AUTH_FIELD_LABEL_CLASS}>
            Country
          </TvLabel>
          <TvSelect
            id="register-country"
            name="country"
            aria-label="Country"
            className="tv-login-page__field"
            data-testid="register-country"
            autoComplete="country"
            value={validation.values.country}
            onChange={handleCountryChange}
            onBlur={validation.handleBlur}
            invalid={validation.touched.country && !!validation.errors.country}
          >
            {COUNTRIES.map((c) => (
              <TvOption key={c.code} value={c.code}>
                {c.label}
              </TvOption>
            ))}
          </TvSelect>
          {validation.touched.country && validation.errors.country ? (
            <TvSmall className="text-danger d-block mt-1">{validation.errors.country}</TvSmall>
          ) : null}
        </TvDiv>

        <TvDiv className="mb-3">
          <TvLabel htmlFor="register-currency" className={AUTH_FIELD_LABEL_CLASS}>
            Currency
          </TvLabel>
          <TvSelect
            id="register-currency"
            name="currency"
            aria-label="Currency"
            className="tv-login-page__field"
            data-testid="register-currency"
            autoComplete="off"
            value={validation.values.currency}
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            invalid={validation.touched.currency && !!validation.errors.currency}
          >
            {CURRENCY_OPTIONS.map((c) => (
              <TvOption key={c.code} value={c.code}>
                {c.label}
              </TvOption>
            ))}
          </TvSelect>
          {validation.touched.currency && validation.errors.currency ? (
            <TvSmall className="text-danger d-block mt-1">{validation.errors.currency}</TvSmall>
          ) : null}
        </TvDiv>

        <TvDiv className="mb-3">
          <TvLabel htmlFor="register-email" className={AUTH_FIELD_LABEL_CLASS}>
            Work Email
          </TvLabel>
          <TvInput
            id="register-email"
            name="email"
            type="email"
            autoComplete="username"
            placeholder="name@company.com"
            aria-label="Work Email"
            className="tv-login-page__field"
            onChange={handleChangeWithTrim}
            onBlur={validation.handleBlur}
            value={validation.values.email}
            invalid={validation.touched.email && !!validation.errors.email}
          />
          {validation.touched.email && validation.errors.email ? (
            <TvSmall className="text-danger d-block mt-1">{validation.errors.email}</TvSmall>
          ) : null}
        </TvDiv>

        <TvDiv className="mb-4">
          <TvLabel htmlFor="register-password" className={AUTH_FIELD_LABEL_CLASS}>
            Password
          </TvLabel>
          <TvDiv className="position-relative">
            <TvInput
              id="register-password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="Create a secure password"
              aria-label="Password"
              className="tv-login-page__field"
              onChange={handleChangeWithTrim}
              onBlur={validation.handleBlur}
              value={validation.values.password}
              invalid={validation.touched.password && !!validation.errors.password}
            />
            <TvIconButton
              type="button"
              color="link"
              className="tv-login-page__password-toggle position-absolute top-50 end-0 translate-middle-y text-muted"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <i className={`mdi ${showPassword ? "mdi-eye-off" : "mdi-eye"} fs-5`} />
            </TvIconButton>
          </TvDiv>
          {validation.touched.password && validation.errors.password ? (
            <TvSmall className="text-danger d-block mt-1">{validation.errors.password}</TvSmall>
          ) : null}
        </TvDiv>

        <TvDiv className="d-grid mb-3">
          <TvButton
            color="primary"
            type="submit"
            disabled={loading}
            className={`tv-login-page__submit${!hasFormValues && !loading ? " disabled" : ""}`}
          >
            {loading ? <Spinner size="sm" color="light" /> : "Create account"}
          </TvButton>
        </TvDiv>

        <TvDiv className="text-center text-lg-start">
          <Link to="/login" className="tv-login-page__text-link fw-semibold text-decoration-none">
            Back to sign in
          </Link>
        </TvDiv>
      </Form>
      ) : (
        <>
          <AuthContinueWithEmailButton onClick={() => setShowEmailForm(true)} />
          <TvDiv className="text-center text-lg-start">
            <Link to="/login" className="tv-login-page__text-link fw-semibold text-decoration-none">
              Back to sign in
            </Link>
          </TvDiv>
        </>
      )}
      </>
      )}
    </AuthSplitLayout>
  );
};

export default Register;
